'use client';

import './zzimcar-products-list.css';
import CheckGradient2020 from '@svg/trip/check_gradient_20_20.svg';
import ArrowDown from '@svg/trip/arrow_down.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getZzimProductList } from '@/utils/actions';
import { ZzimProductEnumCGType, ZzimProductInfoCGType } from '@/types/graphql/root';
import { filterNullUndefined } from '@/utils';
import { isDesktop, isTouchDevice } from '@/utils/platform';
import CuratedTripsSlideButtons from '../main/curated-trips-slide-buttons';
import ArrowRightGray812 from '@/svg/trip/arrow_right_gray_8_12.svg';
import LinkForZzimcarAndroid from '../common/link-for-zzimcar-android';
import usePlatformInfo from '@/app/hooks/usePlatformInfo';

export default function ZzimcarProductsList({
  zzimcarProductsProps,
  surveyId,
}: {
  zzimcarProductsProps: {
    zzimRentCarZone: string | null;
    zzimRentCarZoneName: string | null;
    startDate: any;
    endDate: any;
  };
  surveyId: string;
}) {
  const [isOpenAccordion, setIsOpenAccordion] = useState(false);
  const [hasProducts, setHasProducts] = useState(true);
  const [isFinishSetProducts, setIsFinishSetProducts] = useState(false);

  const [domesticRentCar, setDomesticRentCar] = useState<ZzimProductInfoCGType | null>(null);
  const [domesticAirline, setDomesticAirline] = useState<ZzimProductInfoCGType | null>(null);
  const [overseaAirline, setOverseaAirline] = useState<ZzimProductInfoCGType | null>(null);
  const [tourTaxi, setTourTaxi] = useState<ZzimProductInfoCGType | null>(null);
  const { isMobile } = usePlatformInfo();

  const onClickAccordion = useCallback(() => {
    if (isOpenAccordion) {
      setIsOpenAccordion(false);
    } else {
      setIsOpenAccordion(true);
    }
  }, [isOpenAccordion]);

  useEffect(() => {
    const { zzimRentCarZone, startDate, endDate } = zzimcarProductsProps;
    if (!startDate || !endDate) return;
    const startDayjs = dayjs(dayjs(startDate).format('YYYY-MM-DD 10:00:00'));
    const endDayjs = dayjs(dayjs(endDate).format('YYYY-MM-DD 18:00:00'));
    if (startDayjs.isBefore(Date.now())) {
      console.log('현재보다 이전날짜 일정표라 조회 할 수 없음');
      setHasProducts(false);
      return;
    } else {
      setHasProducts(true);
    }

    function setProducts(products: ZzimProductInfoCGType[]) {
      setDomesticRentCar(
        products.find((p) => {
          return p.tag === ZzimProductEnumCGType.RentcarCGType;
        }) ?? null,
      );
      setDomesticAirline(
        products.find((p) => {
          return p.tag === ZzimProductEnumCGType.DomesticairlineCGType;
        }) ?? null,
      );
      setOverseaAirline(
        products.find((p) => {
          return p.tag === ZzimProductEnumCGType.OverseasairlineCGType;
        }) ?? null,
      );
      setTourTaxi(
        products.find((p) => {
          return p.tag === ZzimProductEnumCGType.TourtaxiCGType;
        }) ?? null,
      );

      setIsFinishSetProducts(true);
    }

    async function getProducts() {
      if (zzimRentCarZone) {
        // 국내
        const data = await getZzimProductList(surveyId, {
          zone: zzimRentCarZone,
          sDate: startDayjs.format('YYYY-MM-DD HH:mm'),
          eDate: endDayjs.format('YYYY-MM-DD HH:mm'),
        });
        if (isTouchDevice()) {
          setProducts(filterNullUndefined(data?.zzimProductListMo));
        } else {
          setProducts(filterNullUndefined(data?.zzimProductList));
        }
      } else {
        // 해외
        const data = await getZzimProductList(surveyId, null);
        if (isTouchDevice()) {
          setProducts(filterNullUndefined(data?.zzimProductListMo));
        } else {
          setProducts(filterNullUndefined(data?.zzimProductList));
        }
      }
    }

    getProducts();
  }, [zzimcarProductsProps.startDate]);

  const hasAnyProduct = useMemo(() => {
    return [domesticRentCar, domesticAirline, overseaAirline, tourTaxi].some((p) => p);
  }, [domesticRentCar, domesticAirline, overseaAirline, tourTaxi]);

  const productsLength = useMemo(() => {
    return [domesticRentCar, domesticAirline, overseaAirline, tourTaxi].filter((v) => v).length;
  }, [domesticRentCar, domesticAirline, overseaAirline, tourTaxi]);

  const overseaAirlineUrl = useMemo(() => {
    if (typeof window !== 'undefined' && isDesktop) return process.env.NEXT_PUBLIC_ZZIMCAR_INT_AIR_PC_URL;
    return process.env.NEXT_PUBLIC_ZZIMCAR_INT_AIR_MOBILE_URL;
  }, [isDesktop]);

  if (!isFinishSetProducts && hasProducts) {
    return (
      <div className='w-full rounded-6pxr h-223pxr px-20pxr py-24pxr'>
        <div className='rounded-6pxr w-full h-full skeleton-item'></div>
      </div>
    );
  }

  const getDomesticRentCarUrl = (rentCar: ZzimProductInfoCGType | null, isMobileDevice: boolean): string => {
    if (rentCar?.redirectUrl) {
      return rentCar.redirectUrl;
    }
    if (isMobileDevice) {
      return process.env.NEXT_PUBLIC_ZZIMCAR_RENTCAR_MOBILE_URL ?? '';
    }
    return process.env.NEXT_PUBLIC_ZZIMCAR_RENTCAR_PC_URL ?? '';
  };

  if (!hasProducts || (isFinishSetProducts && !hasAnyProduct))
    return (
      <>
        <div className={`w-full py-24pxr px-20pxr flex flex-col`}>
          <div className='flex justify-between items-start'>
            <div className='flex flex-col gap-4pxr'>
              <div className='h-20pxr flex items-center gap-4pxr'>
                <CheckGradient2020 />
                <p
                  className='body-12-md'
                  style={{
                    background:
                      'var(--point-point-gradient, linear-gradient(271deg, #33A1FC 0.32%, #2FE8BC 99.61%, #2FE8BC 99.61%))',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  찜카할인혜택
                </p>
              </div>
              <p className='body-16-md text-gray-900'>찜카 상품 한 번 둘러보실래요?</p>
            </div>
            <button
              className='w-20pxr h-20pxr flex items-center justify-center transition-transform'
              style={{
                transform: isOpenAccordion ? 'rotate(-180deg)' : 'rotate(0deg)',
              }}
              onClick={() => onClickAccordion()}
            >
              <ArrowDown />
            </button>
          </div>
          <div
            style={{
              overflow: 'hidden',
              transition: 'height 0.15s',
              height: isOpenAccordion ? 244 : 0,
            }}
          >
            <div className='h-16pxr'></div>
            <div className='w-full flex flex-col gap-12pxr'>
              <LinkForZzimcarAndroid
                href={getDomesticRentCarUrl(domesticRentCar, isMobile)}
                target='_blank'
                className='w-full h-48pxr px-12pxr py-14pxr flex gap-10pxr rounded-6pxr border border-gray-300 bg-white items-center body-14-md'
              >
                <span>🚘</span>
                <p className='grow text-gray-900'>렌트카 보러가기</p>
                <span className='w-16pxr h-16pxr flex items-center justify-center'>
                  <ArrowRightGray812 width={6.4} height={11.2} viewBox='0 0 8 12' />
                </span>
              </LinkForZzimcarAndroid>
              <LinkForZzimcarAndroid
                href={process.env.NEXT_PUBLIC_ZZIMCAR_AIR_URL ?? ''}
                target='_blank'
                className='w-full h-48pxr px-12pxr py-14pxr flex gap-10pxr rounded-6pxr border border-gray-300 bg-white items-center body-14-md'
              >
                <span>✈️</span>
                <p className='grow text-gray-900'>국내 항공 보러가기</p>
                <span className='w-16pxr h-16pxr flex items-center justify-center'>
                  <ArrowRightGray812 width={6.4} height={11.2} viewBox='0 0 8 12' />
                </span>
              </LinkForZzimcarAndroid>
              <LinkForZzimcarAndroid
                href={overseaAirlineUrl ?? ''}
                target='_blank'
                className='w-full h-48pxr px-12pxr py-14pxr flex gap-10pxr rounded-6pxr border border-gray-300 bg-white items-center body-14-md'
              >
                <span>🌍</span>
                <p className='grow text-gray-900'>해외 항공 보러가기</p>
                <span className='w-16pxr h-16pxr flex items-center justify-center'>
                  <ArrowRightGray812 width={6.4} height={11.2} viewBox='0 0 8 12' />
                </span>
              </LinkForZzimcarAndroid>
              <LinkForZzimcarAndroid
                href={process.env.NEXT_PUBLIC_ZZIMCAR_TOUR_TAXI_URL ?? ''}
                target='_blank'
                className='w-full h-48pxr px-12pxr py-14pxr flex gap-10pxr rounded-6pxr border border-gray-300 bg-white items-center body-14-md'
              >
                <span>🚕</span>
                <p className='grow text-gray-900'>투어택시 보러가기</p>
                <span className='w-16pxr h-16pxr flex items-center justify-center'>
                  <ArrowRightGray812 width={6.4} height={11.2} viewBox='0 0 8 12' />
                </span>
              </LinkForZzimcarAndroid>
            </div>
          </div>
        </div>
        <div className='w-full h-10pxr border-t border-b border-gray-300 bg-gray-100'></div>
      </>
    );

  return (
    <>
      <div className={`w-full py-24pxr px-20pxr flex flex-col`}>
        <div className='flex justify-between items-start'>
          <div className='flex flex-col gap-4pxr'>
            <div className='h-20pxr flex items-center gap-4pxr'>
              <CheckGradient2020 />
              <p
                className='body-12-md'
                style={{
                  background:
                    'var(--point-point-gradient, linear-gradient(271deg, #33A1FC 0.32%, #2FE8BC 99.61%, #2FE8BC 99.61%))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                찜카할인혜택
              </p>
            </div>
            <p className='body-16-md text-gray-900'>교통편도 함께 저렴하게 예약해보세요!</p>
          </div>
          <button
            className='w-20pxr h-20pxr flex items-center justify-center transition-transform'
            style={{
              transform: isOpenAccordion ? 'rotate(-180deg)' : 'rotate(0deg)',
            }}
            onClick={() => onClickAccordion()}
          >
            <ArrowDown />
          </button>
        </div>
        <div
          style={{
            overflow: 'hidden',
            transition: 'height 0.15s',
            height: isOpenAccordion ? 127 : 0,
          }}
        >
          <div className='h-16pxr'></div>
          <div className='relative'>
            <div id='zzimcar-product-list' className='h-111pxr overflow-y-hidden overflow-x-scroll flex gap-17pxr'>
              {domesticRentCar && (
                <LinkForZzimcarAndroid
                  href={getDomesticRentCarUrl(domesticRentCar, isMobile)}
                  target='_blank'
                  className='w-217pxr h-111pxr rounded-8pxr border border-[#F4F4F4] shrink-0 p-12pxr flex flex-col gap-7pxr'
                  style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div className='flex gap-4pxr flex-start flex-col w-full'>
                    <span className='w-36pxr h-16pxr flex items-center justify-center bg-[#E8F8E9] rounded-2pxr text-10pxr font-normal leading-[16px] text-[#1EB725]'>
                      렌트카
                    </span>
                    <p className='w-full h-18pxr text-custom-black sub-title-14-bold line-clamp-1'>
                      {domesticRentCar.name}
                    </p>
                    <span className='w-full h-18pxr text-gray-600 body-12-regular line-clamp-1'>
                      {`${domesticRentCar.startDate} ~ ${domesticRentCar.endDate}`}
                    </span>
                  </div>
                  {domesticRentCar.minPrice && (
                    <div className='w-full text-right text-primary sub-title-16-bold'>
                      {`${(+domesticRentCar.minPrice).toLocaleString()}원`}
                    </div>
                  )}
                </LinkForZzimcarAndroid>
              )}
              {domesticAirline && (
                <LinkForZzimcarAndroid
                  href={process.env.NEXT_PUBLIC_ZZIMCAR_AIR_URL ?? ''}
                  target='_blank'
                  className='w-217pxr h-111pxr rounded-8pxr border border-[#F4F4F4] shrink-0 p-12pxr flex flex-col gap-7pxr'
                  style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div className='flex gap-4pxr flex-start flex-col w-full h-60pxr'>
                    <span className='w-48pxr h-16pxr flex items-center justify-center bg-[#EBF6FF] rounded-2pxr text-10pxr font-normal leading-[16px] text-[#3177FF]'>
                      국내 항공
                    </span>
                    <p className='w-full h-18pxr text-custom-black sub-title-14-bold line-clamp-1'>출발지 선택 후</p>
                    <span className='w-full h-18pxr text-gray-600 body-12-regular line-clamp-1'>
                      {`${domesticAirline.startDate} ~ ${domesticAirline.endDate}`}
                    </span>
                  </div>
                  <div className='w-full text-right text-primary sub-title-16-bold'>최저가 보장!</div>
                </LinkForZzimcarAndroid>
              )}
              {overseaAirline && (
                <LinkForZzimcarAndroid
                  href={overseaAirlineUrl ?? ''}
                  target='_blank'
                  className='w-217pxr h-111pxr rounded-8pxr border border-[#F4F4F4] shrink-0 p-12pxr flex flex-col gap-7pxr'
                  style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div className='flex gap-4pxr flex-start flex-col w-full h-60pxr'>
                    <span className='w-48pxr h-16pxr flex items-center justify-center bg-[#EBF6FF] rounded-2pxr text-10pxr font-normal leading-[16px] text-[#3177FF]'>
                      해외 항공
                    </span>
                    <p className='w-full h-18pxr text-custom-black sub-title-14-bold line-clamp-1'>출발지 선택 후</p>
                    <span className='w-full h-18pxr text-gray-600 body-12-regular line-clamp-1'>
                      {`${overseaAirline.startDate} ~ ${overseaAirline.endDate}`}
                    </span>
                  </div>
                  <div className='w-full text-right text-primary sub-title-16-bold'>최저가 보장!</div>
                </LinkForZzimcarAndroid>
              )}
              {tourTaxi && (
                <LinkForZzimcarAndroid
                  href={process.env.NEXT_PUBLIC_ZZIMCAR_TOUR_TAXI_URL ?? ''}
                  target='_blank'
                  className='w-217pxr h-111pxr rounded-8pxr border border-[#F4F4F4] shrink-0 p-12pxr flex flex-col gap-7pxr'
                  style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div className='flex gap-4pxr flex-start flex-col w-full h-60pxr'>
                    <span className='w-45pxr h-16pxr flex items-center justify-center bg-[#FEF6E9] rounded-2pxr text-10pxr font-normal leading-[16px] text-[#F9A825]'>
                      투어택시
                    </span>
                    <p className='w-full h-18pxr text-custom-black sub-title-14-bold line-clamp-1'>{tourTaxi.name}</p>
                    <span className='w-full h-18pxr text-gray-600 body-12-regular line-clamp-1'>
                      {`${tourTaxi.startDate} ~ ${tourTaxi.endDate}`}
                    </span>
                  </div>
                  <div className='w-full text-right text-primary sub-title-16-bold'>
                    {tourTaxi.minPrice && tourTaxi.minPrice}원 (1일당)
                  </div>
                </LinkForZzimcarAndroid>
              )}
              {productsLength > 1 && <CuratedTripsSlideButtons targetId='zzimcar-product-list' />}
            </div>
          </div>
        </div>
      </div>
      <div className='w-full h-10pxr border-t border-b border-gray-300 bg-gray-100'></div>
    </>
  );
}
